import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
} from "types/DefaultRules/DefaultRules";
import {
  IInpatientRuleset,
  IInpatientRulesetResponse,
  IAllInpatientRulesetsResponse,
} from "types/InpatientRuleset";

import { api } from ".";

const inpatientRulesetQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getInpatientRulesets: builder.query<
      IAllInpatientRulesetsResponse,
      { isDefault?: boolean } | null | void
    >({
      query: (params) => {
        const search =
          params && params.isDefault !== undefined
            ? `?isDefault=${params.isDefault}`
            : "";
        return `inpatient-rulesets${search}`;
      },
      providesTags: ["InpatientRulesets"],
    }),
    getInpatientRuleset: builder.query<IInpatientRulesetResponse, string>({
      query: (id) => `inpatient-rulesets/${id}`,
      providesTags: (_result, _error, id) => [
        { type: "InpatientRulesets", id },
      ],
    }),
    getInpatientRulesetsByContract: builder.query<
      IAllInpatientRulesetsResponse,
      { providerId: string; payerId: number }
    >({
      query: ({ providerId, payerId }) =>
        `inpatient-rulesets/by-contract/${providerId}/${payerId}`,
      providesTags: ["InpatientRulesets"],
    }),
    createInpatientRuleset: builder.mutation<
      IInpatientRulesetResponse,
      Omit<
        IInpatientRuleset,
        | "id"
        | "createdAt"
        | "updatedAt"
        | "provider"
        | "payer"
        | "isDefault"
        | "sourceDefaultId"
        | "modifiedFields"
      > & {
        isDefault?: boolean;
        sourceDefaultId?: string | null;
      }
    >({
      query: (body) => ({
        url: "inpatient-rulesets",
        method: "POST",
        body,
      }),
      invalidatesTags: ["InpatientRulesets"],
    }),
    updateInpatientRuleset: builder.mutation<
      IInpatientRulesetResponse,
      {
        id: string;
        body: Partial<
          Omit<
            IInpatientRuleset,
            | "id"
            | "createdAt"
            | "updatedAt"
            | "provider"
            | "payer"
            | "isDefault"
            | "sourceDefaultId"
            | "modifiedFields"
          >
        >;
      }
    >({
      query: ({ id, body }) => ({
        url: `inpatient-rulesets/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["InpatientRulesets", "DefaultRulePropagation"],
    }),
    deleteInpatientRuleset: builder.mutation<any, string>({
      query: (id) => ({
        url: `inpatient-rulesets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["InpatientRulesets", "DefaultRulePropagation"],
    }),
    cloneInpatientRulesetAsDefault: builder.mutation<
      IInpatientRulesetResponse,
      string
    >({
      query: (id) => ({
        url: `inpatient-rulesets/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["InpatientRulesets"],
    }),
    copyInpatientRulesetToContract: builder.mutation<
      IInpatientRulesetResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `inpatient-rulesets/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["InpatientRulesets", "ContractFeeSchedules"],
    }),
    getInpatientRulesetPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `inpatient-rulesets/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `inpatient:${id}` },
      ],
    }),
    applyInpatientRulesetPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `inpatient-rulesets/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "InpatientRulesets",
        { type: "DefaultRulePropagation", id: `inpatient:${id}` },
      ],
    }),
  }),
});

export const {
  useGetInpatientRulesetsQuery,
  useGetInpatientRulesetQuery,
  useGetInpatientRulesetsByContractQuery,
  useCreateInpatientRulesetMutation,
  useUpdateInpatientRulesetMutation,
  useDeleteInpatientRulesetMutation,
  useCloneInpatientRulesetAsDefaultMutation,
  useCopyInpatientRulesetToContractMutation,
  useGetInpatientRulesetPropagationPreviewQuery,
  useLazyGetInpatientRulesetPropagationPreviewQuery,
  useApplyInpatientRulesetPropagationMutation,
} = inpatientRulesetQueries;
